/**
 * Which account the offline store belongs to, and its generation counter.
 *
 * One IndexedDB database per account keeps a second sign-in on the same
 * browser from ever reading or replaying another account's data. The
 * generation is bumped on every wipe and checked on every write-through, so
 * a response that lands after a wipe is discarded; it is kept in
 * localStorage because it must outlive the database it guards.
 */

/** Written by auth.ts setToken(); read here to avoid importing auth.ts. */
const AUTH_USER_KEY = 'auth_user_email';
const LAST_ACCOUNT_KEY = 'offline_last_account';
const GENERATION_PREFIX = 'offline_generation:';

/** The signed-in account, or null when there is no live session. */
export function currentAccountId(): string | null {
  try {
    return localStorage.getItem(AUTH_USER_KEY);
  } catch {
    return null;
  }
}

/** The account whose store was most recently opened, live session or not. */
export function lastAccountId(): string | null {
  try {
    return localStorage.getItem(LAST_ACCOUNT_KEY);
  } catch {
    return null;
  }
}

/** Record the account so a signed-out boot can still name (and wipe) its store. */
export function rememberAccount(accountId: string): void {
  try {
    localStorage.setItem(LAST_ACCOUNT_KEY, accountId);
  } catch {
    // Storage unavailable: the store will not open either, nothing to remember.
  }
}

/** Database name for an account. */
export function databaseNameFor(accountId: string): string {
  return `pa-offline-${accountId}`;
}

/**
 * Current generation for an account; 0 until the first wipe.
 *
 * A stored value that is not a number reads as 0 rather than as a NaN, which
 * equals nothing — not even itself — and would quietly void every write
 * instead of guarding them.
 */
export function currentStoreGeneration(accountId: string): number {
  try {
    const stored = Number(localStorage.getItem(GENERATION_PREFIX + accountId) ?? '0');
    return Number.isFinite(stored) ? stored : 0;
  } catch {
    return 0;
  }
}

/** Advance the generation; every in-flight write-through for the old one is now void. */
export function bumpStoreGeneration(accountId: string): number {
  const next = currentStoreGeneration(accountId) + 1;
  try {
    localStorage.setItem(GENERATION_PREFIX + accountId, String(next));
  } catch {
    // Without storage the store never opens, so there is nothing to guard.
  }
  return next;
}
